import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormWidgetTheme } from "@xapp/stentor-form-widget-channel";

import { FormWidgetConfig } from "../../config";
import { log, err } from "../../utils";
import { SearchStoreProvider, useSearchDispatch, useSearchState } from "../../store";
import { ModalDialog } from "../ModalDialog";
import { FormWidget } from "../FormWidget";
import { WidgetStylesheet } from "../WidgetStylesheet";
import { classNames } from "../../constants/variables";
import SideButton from "../SideButton/SideButton";
import { useAutoOpenTriggers, hasAutoOpenTriggers, DEFAULT_SESSION_KEY } from "../../hooks";
import { isEmbedded } from "../../utils/isEmbedded";

import { StentorApi } from "../../api";

import styles from "./Form.module.scss";

// A syntactically valid triggerSelector that simply never matches anything (e.g. a Studio
// typo) would otherwise keep its MutationObserver watching -- and re-querying the whole page on
// every DOM mutation -- for the widget's entire lifetime. Exported so tests can drive the
// timeout deterministically without duplicating the literal.
export const TRIGGER_SELECTOR_WATCH_TIMEOUT_MS = 30000;

export interface FormProps {
    readonly config: FormWidgetConfig;
    readonly theme?: FormWidgetTheme;
    /**
     * Optional elements that will act as the button to enable the modal search.
     */
    readonly button?: JSX.Element | string;
    readonly buttonElementLookup?: () => Promise<HTMLElement>;
}

/**
 * FormContainer is the main entry point for the Form widget. It provides the context for the
 * form and handles the button click events.
 * It is a wrapper around the Form component.
 */
export const FormContainer: FC<FormProps> = (props) => {
    return (
        <SearchStoreProvider>
            <Form
                config={props.config}
                theme={props.theme}
                button={props.button}
                buttonElementLookup={props.buttonElementLookup}
            />
        </SearchStoreProvider>
    );
};

/**
 * Form is the main component for the Form widget. It provides the context for the
 * form and handles the button click events.
 */
/**
 * Options that can be passed when programmatically opening the form
 */
export interface OpenFormOptions {
    /**
     * Label of a chip to auto-select after the form loads.
     * If the first step of the form contains chips, the form will
     * automatically click the chip matching this label.
     */
    preselectChipLabel?: string;
}

export const Form: FC<FormProps> = (props) => {
    const { config, theme } = props;

    const dispatch = useSearchDispatch();

    const { active, minimized } = useSearchState();

    // Store preselectChipLabel when form is opened programmatically
    const [preselectChipLabel, setPreselectChipLabel] = useState<string | undefined>();

    // Track if user has interacted with the form (for auto-open triggers)
    const [hasInteracted, setHasInteracted] = useState(false);

    // Track if the form was opened by an auto-trigger (vs manually)
    const [openedByTrigger, setOpenedByTrigger] = useState(false);

    // The host page opts into inline rendering by pre-creating `#xapp-form-widget` with the
    // `embedded` class and sizing it itself; App.tsx's renderWidget then renders into that
    // div rather than appending a floating one.
    //
    // Read once at mount, not per render: renderWidget guarantees the element exists before
    // React mounts into it, so there is nothing to wait for, and re-querying the document on
    // every render would do it on every keystroke. It also pins the layout for the session --
    // a host page whose CSS framework or A/B tool churns classes cannot flip the widget
    // between an inline form and a full-screen modal mid-conversation.
    const [embedded] = useState(isEmbedded);

    // `autoOpen.enabled` with no behavior-based triggers configured means "open
    // immediately on load" (docked, no close button). If triggers ARE configured,
    // `enabled` only turns the feature on -- the widget must wait for a trigger
    // to fire (which sets `active` via handleAutoOpen) rather than bypassing it.
    const immediateAutoOpen = !!config.autoOpen?.enabled && !hasAutoOpenTriggers(config.autoOpen);

    // Internal toggle function
    const toggleModal = useCallback(
        (options?: OpenFormOptions) => {
            // Store the preselect label if provided
            if (options?.preselectChipLabel) {
                setPreselectChipLabel(options.preselectChipLabel);
            }
            dispatch({
                type: "toggle-modal-state",
            });
        },
        [dispatch]
    );

    // Event handler for DOM events (onclick, onfocus)
    const handleFocus = useCallback(() => {
        toggleModal();
    }, [toggleModal]);

    // API function that accepts options (for window.xafwControl.openForm)
    const openFormWithOptions = useCallback(
        (options?: OpenFormOptions) => {
            toggleModal(options);
        },
        [toggleModal]
    );

    const formStatus = useCallback(() => {
        return { active, minimized };
    }, [active, minimized]);

    const api = useMemo(() => {
        return StentorApi.getApi({
            key: config.api.key,
            url: config.api.url,
            // Must match the config FormWidget passes below: getApi caches per url+key+intentId, so
            // this and FormWidget's call share one instance only if their intentId agrees. Form also
            // sends FORM_CLOSE from here, which needs the right intentId for overrideKey.
            intentId: config.intentId,
            // Carried through so the preview guarantee lives in one place.
            demoMode: config.api.demoMode,
            // prefetchQuery: config.autoGreeting
        });
    }, [config]);

    const handleClose = useCallback(() => {
        // Clear preselectChipLabel when form closes
        setPreselectChipLabel(undefined);
        // Mark as interacted to prevent auto-open triggers from firing again
        setHasInteracted(true);
        // Only persist dismissal if the form was opened by an auto-trigger
        // This prevents manual opens from blocking future auto-triggers
        if (openedByTrigger) {
            try {
                const sessionKey = config.autoOpen?.sessionKey || DEFAULT_SESSION_KEY;
                sessionStorage.setItem(sessionKey, "true");
            } catch {
                // Private browsing or sessionStorage not available - ignore
            }
            setOpenedByTrigger(false);
        }
        dispatch({
            type: "new-search",
        });
        dispatch({
            type: "set-modal-state",
            active: false,
            minimized: false,
            sendRequest: false,
        });

        api.channelActionRequest({ data: { result: {}, step: "", form: "" } }, "FORM_CLOSE").then(
            (response) => {
                log(`ACTION Request (FORM_CLOSE). Data: Response: ${JSON.stringify(response)}`);
            }
        );
    }, [api, config.autoOpen?.sessionKey, dispatch, openedByTrigger]);

    // Callback for auto-open triggers
    const handleAutoOpen = useCallback(() => {
        setOpenedByTrigger(true);
        dispatch({
            type: "set-modal-state",
            active: true,
            minimized: false,
            sendRequest: true,
        });
    }, [dispatch]);

    // Set up auto-open triggers (delay, scroll, exit intent, inactivity)
    useAutoOpenTriggers({
        config: config.autoOpen,
        isActive: active,
        onOpen: handleAutoOpen,
        hasInteracted,
    });

    // This sets up the button
    useEffect(() => {
        const elements = document.getElementsByClassName(
            classNames.XAPP_FORM_BUTTON
        ) as HTMLCollectionOf<HTMLElement>;

        // we found the button by class
        if (elements.length) {
            log(`Assigning ${elements.length} button(s) by class: ${classNames.XAPP_FORM_BUTTON}`);

            Array.from(elements).forEach((element) => {
                element.onclick = handleFocus;
                element.onfocus = handleFocus;
            });
        } else {
            // we didn't find the button by class, let's try by id
            const el = document.getElementById(classNames.XAPP_FORM_BUTTON);
            if (el) {
                log(`Assigning one button by id: ${classNames.XAPP_FORM_BUTTON})}`);
                el.onclick = handleFocus;
                el.onfocus = handleFocus;
            } else {
                log(`Cannot assign buttons by class or id: ${classNames.XAPP_FORM_BUTTON})}`);
            }
        }

        window.xafwControl = { openForm: openFormWithOptions, formStatus };
    }, [formStatus, handleFocus, openFormWithOptions]);

    // Binds config.triggerSelector to existing elements on the customer's site (e.g. a dead CTA
    // button left over from a prior integration) so the widget can be installed without the
    // customer editing anything. Additive to (never replaces) the xapp-form-button binding above.
    useEffect(() => {
        const triggerSelector = config.triggerSelector;
        if (!triggerSelector) {
            return undefined;
        }

        const boundElements = new WeakSet<Element>();
        const bound: Array<{ element: Element; onClick: (event: MouseEvent) => void }> = [];
        let selectorIsInvalid = false;
        let watchTimeoutId: ReturnType<typeof setTimeout> | undefined;

        const bindElement = (element: Element) => {
            if (boundElements.has(element)) {
                return;
            }
            // Already wired up by the default xapp-form-button binding above -- binding again
            // here would double-dispatch toggle-modal-state on a single click. The reducer
            // toggles from current state, so two dispatches in the same tick cancel out and the
            // modal appears to do nothing. A broad triggerSelector can plausibly also match the
            // customer's existing default button.
            if (
                element.classList.contains(classNames.XAPP_FORM_BUTTON) ||
                element.id === classNames.XAPP_FORM_BUTTON
            ) {
                return;
            }
            boundElements.add(element);

            const onClick = (event: MouseEvent) => {
                // Only suppress navigation for a literal dead link (the common leftover-CTA
                // case) -- never stopPropagation, never overwrite an existing listener/onclick.
                if (
                    element instanceof HTMLAnchorElement &&
                    (element.getAttribute("href") === "#" || element.getAttribute("href") === "")
                ) {
                    event.preventDefault();
                }
                handleFocus();
            };

            element.addEventListener("click", onClick);
            bound.push({ element, onClick });
        };

        const stopWatching = () => {
            observer.disconnect();
            if (watchTimeoutId !== undefined) {
                clearTimeout(watchTimeoutId);
                watchTimeoutId = undefined;
            }
        };

        const bindMatches = () => {
            try {
                document.querySelectorAll(triggerSelector).forEach(bindElement);
            } catch (e) {
                // An invalid selector can never become valid -- log once and stop watching,
                // rather than re-logging to the customer's production console on every
                // subsequent DOM mutation.
                selectorIsInvalid = true;
                err(`Invalid triggerSelector "${triggerSelector}": ${e}`);
                stopWatching();
                return;
            }

            // The customer's element is expected to be a static leftover CTA, not something
            // that repeatedly unmounts/remounts -- once at least one match is found and bound,
            // there is nothing left to watch for. Mirrors FormStepDisplay's external-widget
            // MutationObserver, which disconnects once its one-time job is done; otherwise this
            // would re-run a page-wide querySelectorAll on every DOM mutation (including the
            // widget's own modal open/close) for the rest of the page's life.
            if (bound.length > 0) {
                stopWatching();
            }
        };

        const observer = new MutationObserver(bindMatches);

        bindMatches();

        // The matching element may not exist yet at mount time (late-rendered content, SPA
        // routing) -- watch for it instead of only checking once, unless bindMatches already
        // found (and disconnected for) a match or an invalid selector above.
        if (!selectorIsInvalid && bound.length === 0) {
            observer.observe(document.body, { childList: true, subtree: true });

            // A syntactically valid selector that simply never matches anything (e.g. a Studio
            // typo) would otherwise keep this observer running -- and re-querying the whole page
            // on every DOM mutation -- for the widget's entire lifetime. Give up after a bounded
            // window and log once, so a misconfiguration is discoverable instead of a silent,
            // permanent per-mutation cost.
            watchTimeoutId = setTimeout(() => {
                if (bound.length === 0) {
                    err(
                        `triggerSelector "${triggerSelector}" matched no elements within ${TRIGGER_SELECTOR_WATCH_TIMEOUT_MS}ms - giving up`
                    );
                    stopWatching();
                }
            }, TRIGGER_SELECTOR_WATCH_TIMEOUT_MS);
        }

        return () => {
            stopWatching();
            bound.forEach(({ element, onClick }) => element.removeEventListener("click", onClick));
        };
    }, [config.triggerSelector, handleFocus]);

    let button: JSX.Element;

    // if the passed in a button, use it
    if (props.button) {
        if (typeof props.button === "object") {
            // If they provided a button, we add the onClick
            button = (
                <props.button.type
                    {...props.button.props}
                    onClick={handleFocus}
                    onFocus={handleFocus}
                />
            );
        } else {
            // We are using an ID for the button, just put in a placeholder
            button = <></>;
        }
    } else {
        // no button prop provided so use default search container
        button = <></>;
    }

    return (
        <div className={styles.xapp_form}>
            <WidgetStylesheet theme={theme} />

            {/*
                Hidden when embedded: the side button is a `position: fixed` page-edge tab
                that opens the floating modal, so it would both escape the customer's
                container and offer a second way into a form already on the page.
            */}
            {config.sideButtonLabel && !immediateAutoOpen && !embedded && (
                <SideButton
                    onClick={handleFocus}
                    label={config.sideButtonLabel}
                    position={theme?.sideButton?.position}
                />
            )}

            {button}

            {(active || immediateAutoOpen) && (
                <ModalDialog
                    disableBackdrop={immediateAutoOpen}
                    embedded={embedded}
                    onClose={handleClose}>
                    <FormWidget
                        docked={immediateAutoOpen}
                        embedded={embedded}
                        openedByTrigger={openedByTrigger}
                        config={config}
                        preselectChipLabel={preselectChipLabel}
                        onPreselectChipUsed={() => setPreselectChipLabel(undefined)}
                    />
                </ModalDialog>
            )}
        </div>
    );
};
